/**
 * The course editor's pure helpers, "Edit with AI"
 * (docs/plans/lms-course-editor-plan.md 8.2 to 8.4, 8.8, 6.10, 6.11, 6.13,
 * 16, 18.3 to 18.5; WP-E30): a message's state and its pill word, the
 * "Changed:" line, the cost line, the warnings in plain words, the scope and
 * estimate lines, the composer's own checks (the server re-checks each one),
 * and the plain words for every code an edit route or an edit run can carry.
 * No React, no I/O.
 *
 * The state rule (18.3, 18.5): a message whose outcome is NULL takes its
 * state from run.status, and so does a message that reads 'failed' while its
 * retried run is queued or building again; every other outcome is final.
 *
 * Vocabulary (plan 2.3): message, plan, part, swap, before and after, review,
 * residue; "cap" for a spend ceiling (the LMS naming rule).
 */
import { format } from 'date-fns';
import { centsOf } from '../../../../api/builder';
import type { BuilderSource } from '../../../../api/builder';
import {
  EDITOR_INSTRUCTION_MAX,
  EDITOR_MAX_FILES_PER_MESSAGE,
  EDITOR_MAX_MESSAGES_PER_DRAFT,
} from '../../../../api/editor';
import type { EditEstimate, EditMessage, EditScope } from '../../../../api/editor';
import type { ChecklistItem, CourseSource, GlossaryEntry } from '../../../../content/types';
import { ATTESTATION_LABEL, builderFileProblem, dollars, ERROR_COPY as BUILDER_ERROR_COPY, linkProblem } from '../builder/runModel';

/** A section of the draft as the panel names it (8.2's sections prop). */
export interface EditSectionRef {
  id: string;
  title: string;
}

// ---------------------------------------------------------------------------
// Fixed copy (8.2, 8.8, 9)
// ---------------------------------------------------------------------------

export const PANEL_EXPLANATION =
  'Tell the AI what to change. Each message edits this draft; every change is shown and can be reverted until you publish.';

export const PATIENT_NOTICE =
  "Do not type patient information; your message is sent to the platform's AI provider and kept with this draft's history.";

export const EMPTY_TITLE = 'No AI edits yet.';

export const EMPTY_HINT = "Describe a change, for example: Use 'participant' instead of 'subject' throughout.";

export const MATERIAL_ESTIMATE_NOTE =
  'Attached documents are read first; the message then shows its full estimate before it runs.';

// ---------------------------------------------------------------------------
// Message state (8.3, 18.3, 18.5)
// ---------------------------------------------------------------------------

export type EditMessageState =
  | 'reading'
  | 'ready'
  | 'queued'
  | 'planning'
  | 'writing'
  | 'checking'
  | 'saving'
  | 'applied'
  | 'accepted'
  | 'reverted'
  | 'clarify'
  | 'declined'
  | 'no_change'
  | 'failed'
  | 'cancelled'
  | 'unknown';

/** Run statuses of a message that is still working (the panel polls it). */
const POLLED_RUN_STATUSES = ['uploaded', 'extracting', 'queued', 'structuring', 'authoring', 'assembling', 'repairing', 'writing'];

/** Run statuses that are not terminal (the estimated wait included). */
const LIVE_RUN_STATUSES = [...POLLED_RUN_STATUSES, 'estimated'];

/** The states in which a message is still working (a change to any other state is its end). */
const WORKING_STATES: readonly EditMessageState[] = ['reading', 'queued', 'planning', 'writing', 'checking', 'saving'];

function runStatusOf(message: Pick<EditMessage, 'run'> | null | undefined): string | null {
  const status = message && message.run ? message.run.status : null;
  return typeof status === 'string' ? status : null;
}

function stateFromRun(status: string | null): EditMessageState {
  switch (status) {
    case 'uploaded':
    case 'extracting':
      return 'reading';
    case 'estimated':
      return 'ready';
    case 'queued':
      return 'queued';
    case 'structuring':
      return 'planning';
    case 'authoring':
      return 'writing';
    case 'assembling':
    case 'repairing':
      return 'checking';
    case 'writing':
      return 'saving';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'unknown';
  }
}

/** The state a message card shows (the rule in the file header). */
export function messageState(message: Pick<EditMessage, 'outcome' | 'review' | 'run'>): EditMessageState {
  const status = runStatusOf(message);
  const runLive = status !== null && LIVE_RUN_STATUSES.includes(status);
  if (!message.outcome || (message.outcome === 'failed' && runLive)) return stateFromRun(status);
  switch (message.outcome) {
    case 'applied':
      if (message.review === 'accepted') return 'accepted';
      if (message.review === 'reverted') return 'reverted';
      return 'applied';
    case 'no_change':
      return 'no_change';
    case 'clarify':
      return 'clarify';
    case 'declined':
      return 'declined';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'unknown';
  }
}

/** True while the message's run is working (4.10: the panel polls it every 5 s). */
export function isPolledMessage(message: Pick<EditMessage, 'run'> | null | undefined): boolean {
  const status = runStatusOf(message);
  return status !== null && POLLED_RUN_STATUSES.includes(status);
}

/** True for a state in which the message is still working. */
export function isWorkingState(state: EditMessageState): boolean {
  return WORKING_STATES.includes(state);
}

/** True for a message whose attachments are read and whose estimate waits for the admin's Send. */
export function isReadyMessage(message: Pick<EditMessage, 'outcome' | 'review' | 'run'>): boolean {
  return messageState(message) === 'ready';
}

export type EditPillTone = 'green' | 'amber' | 'red' | 'gray' | 'primary';

/** "Writing 2 of 5" from the run's progress, or "Writing" when the count is unknown. */
export function writingLabel(progress: { sections_total?: number | null; sections_done?: number | null } | null | undefined): string {
  const total = progress && typeof progress.sections_total === 'number' ? progress.sections_total : null;
  const done = progress && typeof progress.sections_done === 'number' ? progress.sections_done : 0;
  if (!total || total < 1) return 'Writing';
  const n = Math.min(done + (done < total ? 1 : 0), total);
  return `Writing ${n} of ${total}`;
}

/** 8.3, 8.8: the pill's word and tone (a pill always carries its word). */
export function statePill(message: Pick<EditMessage, 'outcome' | 'review' | 'run'>): { label: string; tone: EditPillTone } {
  const state = messageState(message);
  switch (state) {
    case 'reading':
      return { label: 'Reading files', tone: 'primary' };
    case 'ready':
      return { label: 'Ready', tone: 'amber' };
    case 'queued':
      return { label: 'Queued', tone: 'primary' };
    case 'planning':
      return { label: 'Planning', tone: 'primary' };
    case 'writing':
      return { label: writingLabel(message.run ? message.run.progress : null), tone: 'primary' };
    case 'checking':
      return { label: 'Checking', tone: 'primary' };
    case 'saving':
      return { label: 'Saving', tone: 'primary' };
    case 'applied':
      return { label: 'Applied', tone: 'amber' };
    case 'accepted':
      return { label: 'Accepted', tone: 'green' };
    case 'reverted':
      return { label: 'Reverted', tone: 'gray' };
    case 'clarify':
      return { label: 'Needs your answer', tone: 'amber' };
    case 'declined':
      return { label: 'Declined', tone: 'gray' };
    case 'no_change':
      return { label: 'No change', tone: 'gray' };
    case 'failed':
      return { label: 'Failed', tone: 'red' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'gray' };
    default:
      return { label: 'Unknown', tone: 'gray' };
  }
}

/** Where a run stopped, in words ("while planning"). */
export function stageWords(stage: string | null | undefined): string | null {
  switch (stage) {
    case 'upload':
      return 'while storing the documents';
    case 'extract':
      return 'while reading the documents';
    case 'estimate':
      return 'while working out the cost';
    case 'structure':
      return 'while planning';
    case 'author':
      return 'while writing the sections';
    case 'assemble':
      return 'while putting the draft together';
    case 'repair':
      return 'while checking and repairing';
    case 'write':
      return 'while saving the draft';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Sections, parts and the "Changed:" line (8.3)
// ---------------------------------------------------------------------------

const PART_WORDS: Record<string, string> = {
  title: 'title',
  why_it_matters: 'why it matters',
  slides: 'slides',
  reading: 'reading',
  what_to_remember: 'what to remember',
  questions: 'questions',
};

const COURSE_FIELD_WORDS: Record<string, string> = {
  learning_objectives: 'objectives',
  not_covered: 'not covered',
  glossary: 'glossary',
  key_takeaways: 'takeaways',
  competency_checklist: 'checklist',
};

/** The 1-based number of a section in the draft, or null. */
export function sectionNumber(id: string | null | undefined, sections: readonly EditSectionRef[]): number | null {
  if (!id) return null;
  const i = sections.findIndex((s) => s.id === id);
  return i >= 0 ? i + 1 : null;
}

/** "2" for a known section, the id itself otherwise. */
function sectionToken(id: string, sections: readonly EditSectionRef[]): string {
  const n = sectionNumber(id, sections);
  return n === null ? id : String(n);
}

/** "section 4" (or "section s9" for an id the draft no longer has). */
export function sectionWords(id: string | null | undefined, sections: readonly EditSectionRef[]): string {
  if (!id) return 'a section';
  return `section ${sectionToken(id, sections)}`;
}

/** 8.3: the scope a message was sent with ("Whole course", "Sections 2, 4"). */
export function scopeLine(scope: EditScope | null | undefined, sections: readonly EditSectionRef[]): string {
  if (!scope || scope.kind !== 'sections') return 'Whole course';
  const ids = Array.isArray(scope.section_ids) ? scope.section_ids : [];
  if (ids.length === 0) return 'No section';
  const tokens = ids.map((id) => sectionToken(id, sections));
  return ids.length === 1 ? `Section ${tokens[0]}` : `Sections ${tokens.join(', ')}`;
}

/**
 * 8.3: "Changed: sections 2, 4 (reading), 7 (slides, questions); glossary;
 * 14 swaps". Neighbouring sections with the same parts share one bracket; a
 * section named with no parts had wording swaps only. "Planned:" instead of
 * "Changed:" while the message is not applied. Null when there is nothing to say.
 */
export function changedLine(message: Pick<EditMessage, 'outcome' | 'plan_summary' | 'diff_summary'>, sections: readonly EditSectionRef[]): string | null {
  const plan = message.plan_summary;
  const diff = message.diff_summary;
  const pieces: string[] = [];

  const planned = plan && Array.isArray(plan.sections) ? plan.sections : [];
  if (planned.length > 0) {
    const groups: Array<{ tokens: string[]; parts: string }> = [];
    for (const entry of planned) {
      const parts = Array.isArray(entry.parts) ? entry.parts : [];
      const words = parts.length === 0 ? 'wording swaps only' : parts.map((p) => PART_WORDS[p] ?? String(p).replace(/_/g, ' ')).join(', ');
      const last = groups[groups.length - 1];
      const token = sectionToken(entry.id, sections);
      if (last && last.parts === words) last.tokens.push(token);
      else groups.push({ tokens: [token], parts: words });
    }
    const count = planned.length;
    pieces.push(`${count === 1 ? 'section' : 'sections'} ${groups.map((g) => `${g.tokens.join(', ')} (${g.parts})`).join(', ')}`);
  }

  const added = plan && typeof plan.added === 'number' ? plan.added : diff ? diff.sections_added : 0;
  if (added > 0) pieces.push(`${added} section${added === 1 ? '' : 's'} added`);

  const fields = plan && Array.isArray(plan.course_level) ? plan.course_level : [];
  if (fields.length > 0) pieces.push(fields.map((f) => COURSE_FIELD_WORDS[f] ?? String(f).replace(/_/g, ' ')).join(', '));

  const swaps = diff && typeof diff.swaps_applied === 'number' && diff.swaps_applied > 0 ? diff.swaps_applied : plan && typeof plan.swaps === 'number' ? plan.swaps : 0;
  if (swaps > 0) pieces.push(`${swaps} swap${swaps === 1 ? '' : 's'}`);

  if (pieces.length === 0) return null;
  return `${message.outcome === 'applied' ? 'Changed' : 'Planned'}: ${pieces.join('; ')}`;
}

/** The plan's one-sentence change note, or null (18.5: '' when absent). */
export function changeNoteOf(message: Pick<EditMessage, 'plan_summary'>): string | null {
  const note = message.plan_summary ? message.plan_summary.change_note : null;
  return typeof note === 'string' && note.trim() ? note.trim() : null;
}

/**
 * 8.3: the cost, estimate and actual ("Estimate up to $1.80 . planned $0.95
 * . spent $0.84"). planned_cents is null until the re-price (18.5).
 */
export function costLine(message: Pick<EditMessage, 'run'>): string | null {
  const run = message.run;
  if (!run) return null;
  const parts: string[] = [];
  if (run.estimate_cents !== null && run.estimate_cents !== undefined) parts.push(`Estimate up to ${dollars(run.estimate_cents)}`);
  if (run.planned_cents !== null && run.planned_cents !== undefined) parts.push(`planned ${dollars(run.planned_cents)}`);
  const spent = centsOf(run.spent_cents);
  if (spent > 0 || run.status === 'done' || run.status === 'failed' || run.status === 'cancelled') parts.push(`spent ${dollars(spent)}`);
  if (parts.length === 0) return null;
  const line = parts.join(' \u00b7 ');
  return line.charAt(0).toUpperCase() + line.slice(1);
}

// ---------------------------------------------------------------------------
// Warnings (Appendix D, 18.4, 18.5: unknown kinds render generically)
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Where a residue hit sits, in words ("section 4, slide s4-3"). */
export function elementWords(
  elementKind: string | null,
  elementRef: string | null,
  sectionId: string | null,
  sections: readonly EditSectionRef[]
): string {
  const ref = elementRef ?? '';
  const inSection = sectionId ? `${sectionWords(sectionId, sections)}, ` : '';
  switch (elementKind) {
    case 'slide':
      return `${inSection}slide ${ref}`;
    case 'reading_paragraph': {
      const m = /:p(\d+)$/.exec(ref);
      return `${inSection}reading paragraph ${m ? m[1] : ref}`;
    }
    case 'question': {
      const m = /^q(\d+)$/.exec(ref);
      return `${inSection}question ${m ? m[1] : ref}`;
    }
    case 'section': {
      const part = ref.includes(':') ? ref.slice(ref.indexOf(':') + 1) : '';
      const words = part ? PART_WORDS[part] ?? part.replace(/_/g, ' ') : '';
      return words ? `${inSection}${words}` : sectionWords(sectionId, sections);
    }
    case 'objective': {
      const m = /^o(\d+)$/.exec(ref);
      return `objective ${m ? Number(m[1]) + 1 : ref}`;
    }
    case 'takeaway': {
      const m = /^t(\d+)$/.exec(ref);
      return `takeaway ${m ? Number(m[1]) + 1 : ref}`;
    }
    case 'glossary':
      return `the glossary entry ${ref.startsWith('g:') ? ref.slice(2) : ref}`;
    case 'not_covered':
      return 'the "not covered" line';
    case 'competency_item':
      return `checklist item ${ref}`;
    default: {
      const kind = elementKind ? elementKind.replace(/_/g, ' ') : 'element';
      return `${inSection}${kind} ${ref}`.trim();
    }
  }
}

/** One warning of a message in plain words. */
export function warningText(warning: unknown, sections: readonly EditSectionRef[]): string | null {
  if (!isRecord(warning)) return null;
  const kind = str(warning.kind);
  if (!kind) return null;
  const sectionId = str(warning.section_id);
  switch (kind) {
    case 'residue':
      return `The old wording still appears in ${elementWords(str(warning.element_kind), str(warning.element_ref), sectionId, sections)}.`;
    case 'lint_rose': {
      const before = num(warning.before) ?? 0;
      const after = num(warning.after) ?? 0;
      const n = Math.max(0, after - before);
      return `${n} new lint problem${n === 1 ? '' : 's'}.`;
    }
    case 'swap_outside_reach': {
      const find = str(warning.find) ?? 'the old wording';
      const count = num(warning.count) ?? 0;
      return `"${find}" still appears ${count} time${count === 1 ? '' : 's'} outside the parts this message changed (for example the glossary, the objectives or the takeaways): change those by hand, or send a message that names them.`;
    }
    case 'closing_decision':
      return `The closing section (${sectionWords(sectionId, sections)}) was changed: read it before publishing.`;
    default:
      return `Check: ${kind.replace(/_/g, ' ')}${sectionId ? ` in ${sectionWords(sectionId, sections)}` : ''}.`;
  }
}

/** Every warning of a message in plain words (codes, ids and counts only arrive; G26). */
export function warningLines(warnings: unknown, sections: readonly EditSectionRef[]): string[] {
  if (!Array.isArray(warnings)) return [];
  return warnings.map((w) => warningText(w, sections)).filter((t): t is string => Boolean(t));
}

// ---------------------------------------------------------------------------
// The estimate before Send and the composer's checks (8.2, 6.2, 16)
// ---------------------------------------------------------------------------

export interface EditorLimits {
  maxChars: number;
  maxFiles: number;
  maxBytes: number | undefined;
  maxMessages: number;
  usedMessages: number | null;
}

/** The live limits from an estimate, or the Appendix B defaults. */
export function limitsOf(estimate: EditEstimate | null | undefined): EditorLimits {
  const l = estimate && estimate.limits ? estimate.limits : null;
  const pos = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback);
  return {
    maxChars: pos(l ? l.max_instruction_chars : null, EDITOR_INSTRUCTION_MAX),
    maxFiles: pos(l ? l.max_files : null, EDITOR_MAX_FILES_PER_MESSAGE),
    maxBytes: l && typeof l.max_bytes_per_file === 'number' && l.max_bytes_per_file > 0 ? l.max_bytes_per_file : undefined,
    maxMessages: pos(l ? l.max_messages : null, EDITOR_MAX_MESSAGES_PER_DRAFT),
    usedMessages: l && typeof l.used_messages === 'number' ? l.used_messages : null,
  };
}

/** 8.2: "12 of 30 messages used on this draft". */
export function countsLine(used: number, max: number): string {
  return `${used} of ${max} message${max === 1 ? '' : 's'} used on this draft`;
}

/** The figure a Send in this scope is judged by, or null (a sections scope with nothing chosen). */
export function scopedCentsOf(estimate: EditEstimate, kind: 'course' | 'sections', chosen: number): number | null {
  if (kind === 'course') return centsOf(estimate.estimate.whole_course_cents);
  if (chosen <= 0) return null;
  return centsOf(estimate.estimate.scoped_cents);
}

/**
 * 8.2: "Up to $1.80 for one section; up to $12.20 for the whole course; cap
 * $20.00 per message", or, for chosen sections, their bound first.
 */
export function estimateLine(estimate: EditEstimate | null | undefined, kind: 'course' | 'sections', chosen: number): string | null {
  if (!estimate || !estimate.estimate) return null;
  const whole = dollars(estimate.estimate.whole_course_cents);
  const cap = dollars(estimate.cap_cents);
  if (kind === 'sections' && chosen > 0) {
    const what = chosen === 1 ? 'the chosen section' : `the ${chosen} chosen sections`;
    return `Up to ${dollars(estimate.estimate.scoped_cents)} for ${what}; up to ${whole} for the whole course; cap ${cap} per message`;
  }
  return `Up to ${dollars(estimate.estimate.one_section_cents)} for one section; up to ${whole} for the whole course; cap ${cap} per message`;
}

/**
 * The spend check before Send (the server refuses the same with 429 and the
 * guard's code): the plain-words reason, or null.
 */
export function sendCapProblem(estimate: EditEstimate | null | undefined, kind: 'course' | 'sections', chosen: number): string | null {
  if (!estimate || !estimate.estimate) return null;
  const figure = scopedCentsOf(estimate, kind, chosen);
  if (figure === null) return null;
  const narrower = kind === 'course' ? 'Choose only some sections' : 'Choose fewer sections';
  if (typeof estimate.cap_cents === 'number' && figure > estimate.cap_cents) {
    return `This message could cost up to ${dollars(figure)}, over the ${dollars(estimate.cap_cents)} per-message cap. ${narrower}, or ask for the cap to be raised.`;
  }
  const caps = estimate.caps;
  if (caps && typeof caps.daily_remaining_cents === 'number' && figure > caps.daily_remaining_cents) {
    return `Only ${dollars(caps.daily_remaining_cents)} is left under today's cap for your network (the day is counted in UTC). ${narrower}, or try again tomorrow.`;
  }
  if (caps && typeof caps.monthly_remaining_cents === 'number' && figure > caps.monthly_remaining_cents) {
    return `Only ${dollars(caps.monthly_remaining_cents)} is left under this month's cap. ${narrower}, or ask for the cap to be raised.`;
  }
  return null;
}

export interface ComposerInput {
  text: string;
  maxChars: number;
  kind: 'course' | 'sections';
  chosen: number;
  files: File[];
  links: string[];
  maxFiles: number;
  maxBytes?: number;
  attested: boolean;
}

/** The composer's own checks, in order (the server re-checks every one; 6.2). Empty when Send may go. */
export function composerProblems(input: ComposerInput): string[] {
  const problems: string[] = [];
  const text = input.text.trim();
  if (!text) problems.push('Write what to change.');
  if (text.length > input.maxChars) problems.push(`The message is longer than ${input.maxChars.toLocaleString()} characters.`);
  if (input.kind === 'sections' && input.chosen <= 0) problems.push('Choose at least one section, or edit the whole course.');
  const attached = input.files.length + input.links.length;
  if (attached > input.maxFiles) problems.push(`A message takes at most ${input.maxFiles} documents and web links together.`);
  if (input.files.some((f) => builderFileProblem(f, input.maxBytes))) problems.push('Remove or replace the documents marked below.');
  if (input.links.some((l) => linkProblem(l))) problems.push('Remove the web links marked below.');
  if (attached > 0 && !input.attested) problems.push(`Tick "${ATTESTATION_LABEL}".`);
  return problems;
}

// ---------------------------------------------------------------------------
// Attachments on a message (6.12)
// ---------------------------------------------------------------------------

/** A source's extraction outcome in a word and a tone. */
export function extractionWord(source: Pick<BuilderSource, 'extraction_status' | 'withdrawn_at'>): { label: string; tone: EditPillTone } {
  if (source.withdrawn_at) return { label: 'Withdrawn', tone: 'gray' };
  switch (source.extraction_status) {
    case 'done':
      return { label: 'Read', tone: 'green' };
    case 'refused':
      return { label: 'Refused', tone: 'red' };
    case 'failed':
      return { label: 'Not read', tone: 'red' };
    default:
      return { label: 'Waiting', tone: 'gray' };
  }
}

// ---------------------------------------------------------------------------
// Course-level diff helpers (8.4)
// ---------------------------------------------------------------------------

export interface KeyedChanges<T> {
  added: T[];
  removed: T[];
  changed: Array<{ key: string; before: T; after: T }>;
}

function keyedChanges<T>(before: T[] | null | undefined, after: T[] | null | undefined, keyOf: (item: T) => string): KeyedChanges<T> {
  const b = Array.isArray(before) ? before : [];
  const a = Array.isArray(after) ? after : [];
  const beforeMap = new Map(b.map((item) => [keyOf(item), item]));
  const afterMap = new Map(a.map((item) => [keyOf(item), item]));
  const out: KeyedChanges<T> = { added: [], removed: [], changed: [] };
  a.forEach((item) => {
    const k = keyOf(item);
    const old = beforeMap.get(k);
    if (old === undefined) out.added.push(item);
    else if (JSON.stringify(old) !== JSON.stringify(item)) out.changed.push({ key: k, before: old, after: item });
  });
  b.forEach((item) => {
    if (!afterMap.has(keyOf(item))) out.removed.push(item);
  });
  return out;
}

/** Glossary terms added, removed or changed (matched by term, case-insensitive). */
export function glossaryChanges(before: GlossaryEntry[] | null | undefined, after: GlossaryEntry[] | null | undefined): KeyedChanges<GlossaryEntry> {
  return keyedChanges(before, after, (g) => String(g && g.term ? g.term : '').trim().toLowerCase());
}

/** Checklist items added, removed or changed (matched by id). */
export function checklistChanges(before: ChecklistItem[] | null | undefined, after: ChecklistItem[] | null | undefined): KeyedChanges<ChecklistItem> {
  return keyedChanges(before, after, (c) => String(c && c.id ? c.id : ''));
}

/** Sources added or removed (matched by title and address). */
export function sourceChanges(before: CourseSource[] | null | undefined, after: CourseSource[] | null | undefined): KeyedChanges<CourseSource> {
  return keyedChanges(before, after, (s) => `${String(s && s.title ? s.title : '').trim().toLowerCase()}|${String(s && s.url ? s.url : '').trim()}`);
}

/** The settings whose value differs: [{ key, before, after }]. */
export function settingChanges(before: unknown, after: unknown): Array<{ key: string; before: string; after: string }> {
  const b = isRecord(before) ? before : {};
  const a = isRecord(after) ? after : {};
  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)])).sort();
  const show = (v: unknown) => (v === undefined || v === null ? 'not set' : typeof v === 'string' ? v : JSON.stringify(v));
  return keys.filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k])).map((k) => ({ key: k, before: show(b[k]), after: show(a[k]) }));
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** "25 Sep 2026, 14:05" (the viewer's local time), or an empty string. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return format(d, 'd MMM yyyy, HH:mm');
}

// ---------------------------------------------------------------------------
// Error copy (Appendix A, 6.2, 6.11, 6.13, 18.5)
// ---------------------------------------------------------------------------

/** 6.11: the stale-revert copy, as the plan writes it. */
export const REVERT_STALE_COPY =
  'The draft changed after this edit, by hand or by a later message: revert the later messages first, or change it by hand.';

/** 18.5 (6.9 step 1 narrowed): what a message that met a changed draft says. */
export const DRAFT_CHANGED_COPY =
  'The draft changed while this message was running (by hand or by another message), so nothing was written. Send the message again.';

/** The editor's own codes, and the editor's words for the builder codes its routes share. */
export const EDITOR_ERROR_COPY: Record<string, string> = {
  ...BUILDER_ERROR_COPY,
  LMS_EDITOR_DISABLED: 'Edit with AI is switched off for the Learning Center.',
  LMS_EDITOR_MESSAGE_LIMIT:
    'This draft has used all of its AI messages. Edit it by hand, or publish it and start a new draft.',
  LMS_EDITOR_NOT_REVIEWABLE:
    'This message cannot be accepted or reverted now: it was not applied, or it was already reverted. Reload the draft to see where it stands.',
  LMS_EDITOR_REVERT_STALE: REVERT_STALE_COPY,
  LMS_EDITOR_REVERT_MISMATCH:
    'The draft could not be restored exactly to the state before this edit, so the revert was not recorded. Reload the draft and check it; tell the platform owner if it happens again.',
  LMS_EDITOR_DRAFT_CHANGED: DRAFT_CHANGED_COPY,
  LMS_EDITOR_MESSAGE_MISSING: 'The record of this message could not be found, so it stopped before spending anything. Send it again.',
  LMS_EDITOR_PLAN_INVALID:
    'The AI could not make a usable plan for this message, twice (it named sections or parts outside what you allowed). Rephrase the message or change its scope, and send it again.',
  LMS_EDITOR_SCOPE_VIOLATION:
    'The edit changed something outside what its plan named, so the code stopped it and nothing was written. This is a fault in the editor, not in your message: tell the platform owner.',
  LMS_BUILDER_DISABLED: 'Edit with AI needs the course builder, which is switched off for the Learning Center.',
  LMS_BUILDER_NETWORK_NOT_ALLOWED: 'Edit with AI is not switched on for your network.',
  LMS_BUILDER_ATTESTATION_REQUIRED: `Tick "${ATTESTATION_LABEL}" before the documents are sent.`,
  LMS_BUILDER_RATE_LIMITED: 'Too many messages were sent in the last ten minutes. Wait a few minutes and send it again.',
  LMS_BUILDER_RATE_LIMIT_UNAVAILABLE:
    'Edit with AI could not check its rate limit, so the message was not sent (it never runs unmetered). Try again in a few minutes.',
  LMS_BUILDER_BUDGET_CHECK_UNAVAILABLE:
    'Edit with AI could not check its spending caps, so nothing was sent (it never runs unmetered). Try again in a few minutes.',
  LMS_BUILDER_RUN_CAP_EXCEEDED:
    'This message would cost more than the per-message cap, so it did not run (or it stopped after planning, before any rewrite). Narrow it to fewer sections, or ask for the cap to be raised.',
  LMS_BUILDER_DAILY_BUDGET_EXCEEDED:
    "Today's spending cap for your network is reached. Send the message again tomorrow (the day is counted in UTC).",
  LMS_BUILDER_MONTHLY_BUDGET_EXCEEDED:
    "This month's spending cap is reached. Ask for the cap to be raised, or send the message again next month.",
  LMS_BUILDER_QUEUE_UNAVAILABLE: 'Edit with AI could not reach its job queue. Nothing was spent. Try again in a few minutes.',
  LMS_BUILDER_UPLOAD_FAILED: 'The documents could not be stored. Nothing was spent. Send the message again.',
  LMS_CONFLICT:
    'Another message or a course builder run is working on this course, or the message is not in a state that allows this. Wait for it to finish, then try again.',
  LMS_IMMUTABLE: 'This version is published and cannot be edited. Start a new draft from the course page.',
  LMS_ADMIN_REQUIRED: 'Only Learning Center admins can edit a course with AI.',
  LMS_NOT_FOUND: 'The message or the draft was not found.',
  LMS_INVALID: 'Something in the message was not accepted; the reason is below.',
};

/** Plain words for a code, or `fallback` when the code has none. */
export function editErrorCopy(code: string | null | undefined, fallback = 'Edit with AI could not complete the request.'): string {
  if (code && Object.prototype.hasOwnProperty.call(EDITOR_ERROR_COPY, code)) return EDITOR_ERROR_COPY[code];
  return fallback;
}

function comparable(text: string): string {
  return text.trim().replace(/[.\s]+$/, '').toLowerCase();
}

/**
 * What the admin reads for a refused request or a failed run: the code's
 * plain words, then the server's own words when they add something.
 */
export function explainEditError(code: string | null | undefined, message: string | null | undefined, fallback?: string): string {
  const detail = (message || '').trim();
  const known = Boolean(code && Object.prototype.hasOwnProperty.call(EDITOR_ERROR_COPY, code));
  const copy = editErrorCopy(code, fallback ?? (detail || undefined));
  if (!known || !detail || comparable(detail) === comparable(copy)) return copy;
  return `${copy} (${detail})`;
}
